import { AppRoleName } from '../types/app-role'
import { localStorageProp } from '@tempots/dom'

export interface AgencyMembership {
  agencyId: string
  role: string
  joinedAt?: string
}

export interface RedactionPreferences {
  phoneNumbers: boolean
  socialLinks: boolean
  emails: boolean
  urls: boolean
  keywords: string[]
  caseSensitive: boolean
}

export interface UserProfile {
  name?: string
  phone?: string
  email?: string
  linkedIn?: string
  redactionPreferences?: RedactionPreferences
}

export interface AuthUser {
  id: string
  email: string
  name?: string
  /** @deprecated Use name instead */
  fullName?: string
  role?: AppRoleName
  /** @deprecated Use role instead */
  appRole?: AppRoleName
  image?: string | null
  emailVerified: boolean
  banned?: boolean
  createdAt?: string
  updatedAt?: string
}

/**
 * Auth state managed by Better Auth cookie sessions.
 * Tokens are no longer stored client-side — sessions are managed via httpOnly cookies.
 */
export interface AuthState {
  /** The current user, fetched via getSession(). null when not signed in. */
  user: AuthUser | null
  /** Last auth-related message (e.g. "Registration successful") */
  lastMessage: string | null
  /** Email awaiting verification during sign-up flow */
  pendingVerificationEmail: string | null
  /** Whether the user's email is verified */
  emailVerified: boolean
  /** Active organization ID (persisted for quick access) */
  activeOrganizationId: string | null
}

const authState = localStorageProp<AuthState>({
  key: 'rh:auth',
  defaultValue: {
    user: null,
    lastMessage: null,
    pendingVerificationEmail: null,
    emailVerified: false,
    activeOrganizationId: null,
  },
})

export function setAuthState(state: AuthState) {
  authState.set(state)
}

export function setEmailVerificationState(verified: boolean) {
  authState.update(as => ({
    ...as,
    emailVerified: verified,
    user: as.user ? { ...as.user, emailVerified: verified } : as.user,
  }))
}

export function setEmailVerified() {
  setEmailVerificationState(true)
}

export function updateAuthUser(user: AuthUser) {
  authState.update(as => {
    // Preserve emailVerified from the existing state if the incoming value is
    // undefined — server endpoints that don't return emailVerified should never
    // accidentally clear the verified status.
    const emailVerified =
      user.emailVerified != null
        ? user.emailVerified
        : (as.user?.emailVerified ?? as.emailVerified)
    return {
      ...as,
      user: { ...user, emailVerified: emailVerified === true },
      emailVerified: emailVerified === true,
    }
  })
}

export function storeSessionUser(user: AuthUser) {
  authState.update(as => ({
    ...as,
    user,
    emailVerified: user.emailVerified ?? false,
  }))
}

export function setActiveOrganizationId(orgId: string | null) {
  authState.update(as => ({
    ...as,
    activeOrganizationId: orgId,
  }))
}

export function clearAuthState() {
  authState.set({
    user: null,
    lastMessage: null,
    pendingVerificationEmail: null,
    emailVerified: false,
    activeOrganizationId: null,
  })
}

export function getAuthState() {
  return authState.value
}

export function getActiveOrganizationId(): string | null {
  return authState.value.activeOrganizationId
}

/**
 * @deprecated Tokens are no longer stored. Sessions are cookie-based.
 */
export function getAccessToken(): string | null {
  return null
}

export function getPrimaryAgencyId(): string | null {
  // Use the persisted active organization ID
  return authState.value.activeOrganizationId
}

export function setLastAuthMessage(message: string | null) {
  authState.update(as => ({
    ...as,
    lastMessage: message,
  }))
}

export function setPendingVerificationEmail(email: string | null) {
  authState.update(as => ({
    ...as,
    pendingVerificationEmail: email,
  }))
}

export { authState }
